import { useKnockFeed } from '@knocklabs/react';
import { RiCheckLine, RiNotification3Line, RiNotificationOffLine } from '@remixicon/react';
import { Key, useEffect, useRef } from 'react';
import { Dialog, DialogTrigger } from 'react-aria-components';
import { AgButton, AgSelect } from 'src/components/ui';
import { AgPopover } from 'src/components/ui/AgPopover';
import { useKnockReady } from 'src/providers/KnockNotificationProvider';

import AiThreadNotificationCell, { isAiThreadNotification } from './AiThreadNotificationCell';
import AppErrorNotificationCell, { isAppErrorNotification } from './AppErrorNotificationCell';
import OrderNotificationCell from './OrderNotificationCell';
import ZatannaSyncFailedCell, { isZatannaSyncFailedNotification } from './ZatannaSyncFailedCell';

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Unread', value: 'unread' },
  { label: 'Read', value: 'read' },
];

export default function NotificationMenu() {
  const isReady = useKnockReady();

  if (!isReady) return null;

  return (
    <DialogTrigger>
      <NotificationButton />
      <AgPopover placement="bottom end" className="w-130 p-0 rounded-2xl border-none shadow-lg">
        <Dialog className="outline-none">
          <NotificationFeedContent />
        </Dialog>
      </AgPopover>
    </DialogTrigger>
  );
}

export function NotificationButton() {
  const { feedClient, useFeedStore } = useKnockFeed();
  const unreadCount = useFeedStore((state) => state.metadata.unread_count);

  useEffect(() => {
    feedClient.fetch().catch((err) => {
      console.error('[Knock] Feed fetch error:', err);
    });
  }, [feedClient]);

  return (
    <AgButton
      data-testid="notification-bell-btn"
      variant="ghost"
      size="icon-sm"
      className="relative overflow-visible rounded-[10px] border border-transparent text-neutral-alpha-8 data-pressed:border-[#808F48] data-pressed:bg-transparent"
    >
      <RiNotification3Line size={16} />
      {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 z-10 h-2 w-2 rounded-full bg-red-text" />}
    </AgButton>
  );
}

function NotificationFeedContent() {
  const { feedClient, useFeedStore } = useKnockFeed();
  const items = useFeedStore((state) => state.items);
  const unreadCount = useFeedStore((state) => state.metadata.unread_count);
  const unseenCount = useFeedStore((state) => state.metadata.unseen_count);
  const hasMarkedSeen = useRef(false);

  // Mark all items as seen when the popover opens (prevents email escalation)
  useEffect(() => {
    if (unseenCount > 0 && !hasMarkedSeen.current) {
      hasMarkedSeen.current = true;
      feedClient.markAllAsSeen().catch((err) => {
        console.error('[Knock] Mark all as seen error:', err);
      });
    }
  }, [feedClient, unseenCount]);

  const handleFilterChange = (key: Key) => {
    const status = String(key) as 'all' | 'unread' | 'read';
    feedClient.fetch({ status }).catch((err) => {
      console.error('[Knock] Feed filter error:', err);
    });
  };

  return (
    <div className="flex flex-col min-h-87">
      {/* Header — 48px per Figma */}
      <div className="flex items-center justify-between h-12 px-5">
        <div className="flex items-center gap-2">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">Notifications</span>
          <AgSelect
            options={FILTER_OPTIONS}
            defaultSelectedKey="all"
            onSelectionChange={handleFilterChange}
            variant="ghost"
            size="sm"
            shape="rounded"
            className="flex-row gap-0"
            triggerClassName="h-6 px-2 text-12 rounded-lg bg-black/[0.04] gap-1"
            listBoxClassName="p-1"
            aria-label="Filter notifications"
          />
        </div>
        <AgButton
          variant="ghost"
          size="sm"
          isDisabled={unreadCount === 0}
          onPress={() =>
            feedClient.markAllAsRead().catch((err) => {
              console.error('[Knock] Mark all as read error:', err);
            })
          }
          className="gap-1 text-brand-9 hover:text-brand-11"
        >
          <RiCheckLine size={16} />
          Mark all as read
        </AgButton>
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-6">
            <RiNotificationOffLine size={44} className="text-neutral-alpha-9" />
            <div className="flex flex-col items-center gap-1">
              <span className="text-16 font-medium text-gray-900">No notifications yet</span>
              <span className="text-14 text-neutral-alpha-9">
                We&apos;ll let you know when we&apos;ve got something new for you.
              </span>
            </div>
          </div>
        ) : (
          items.map((item) => {
            const onMarkAsRead = () => feedClient.markAsRead(item);
            const data = item.data as { type?: string } | null;
            if (isAiThreadNotification(data)) {
              return <AiThreadNotificationCell key={item.id} item={item} onMarkAsRead={onMarkAsRead} />;
            }
            if (isZatannaSyncFailedNotification(data)) {
              return <ZatannaSyncFailedCell key={item.id} item={item} onMarkAsRead={onMarkAsRead} />;
            }
            if (isAppErrorNotification(data)) {
              return <AppErrorNotificationCell key={item.id} item={item} onMarkAsRead={onMarkAsRead} />;
            }
            return <OrderNotificationCell key={item.id} item={item} onMarkAsRead={onMarkAsRead} />;
          })
        )}
      </div>
    </div>
  );
}
